// TenantCostDashboard — admin / MSP-scoped page at /admin/tenant-cost.
// Surfaces per-tenant, per-day cost from the Databricks-written ATS mirror
// SilverM365TenantCostDaily (GetTenantCostDaily). Overview = all authorized tenants
// ranked by $; select a tenant to drill into its per-day trend + a line-item × day cost
// matrix (TenantCostMatrix): "Log data" rows = the log-driven components broken out by log
// feed (D37 mirror SilverM365TenantLogTypeCostDaily via GetTenantLogTypeCostDaily); "Other"
// rows = the remaining components (storage/enrichment/portal/shared-infra) from the main
// cost rows — so the matrix grand total reconciles to the tenant's total cost.
//
// ALPHA / internal cost intelligence — the prominent banner makes clear these figures
// are an incomplete internal cost-to-serve estimate and do NOT represent client charges.
// A tenant's daily cost = SUM(allocatedUsd) across its cost_component rows; allocatedUsd
// is null ("cost pending") until the cost team feeds that day's figure; is_final marks a
// settled/auditable row.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ScaleIcon,
  UsersIcon,
  ShieldCheckIcon,
  KeyIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { getTenantCostDaily, getTenantLogTypeCostDaily, getCostReconciliation, getTenantProfile, fetchManagedTenants } from '../api/client';
import { RefreshButton, RefreshingBanner, LoadingCard, dataFadeClass } from './ui/LoadingState';
import { useAuth } from '../hooks/useAuth';

const PRESETS = [
  { id: 7, label: '7d' },
  { id: 30, label: '30d' },
  { id: 90, label: '90d' },
];

// Canonical cost-component registry — the full set of cost buckets in the cost model,
// from the Shared Cost Fact Contribution Contract (§Component registry). Every expected
// bucket is shown in the display even before its $ is fed, so the picture reflects the
// whole cost-to-serve model rather than only the components onboarded so far.
//   status 'live'    = the pipeline is producing rows (dollars may still be cost-TBD)
//   status 'planned' = registered bucket, no rows landing yet (not onboarded)
// Updated for D37 (2026-07-08): the single Databricks-compute bucket was split by measured
// per-job DBU into ingest / detection-streaming / detection-cron / enrichment; `overhead`
// was renamed `shared_infrastructure` (now purely un-attributable always-on infra — idle
// pool VMs, keep-warm, ~85% idle SQL, Log Analytics). `databricks_compute` is a non-facing
// CARRIER only and never appears in the fact table, so it is not listed here.
const EXPECTED_COMPONENTS = [
  { key: 'ingest_compute',        label: 'Ingest compute',        team: 'Databricks', status: 'live' },
  { key: 'detection_streaming',   label: 'Detection (streaming)', team: 'Databricks', status: 'live' },
  { key: 'detection_cron',        label: 'Detection (cron)',      team: 'Databricks', status: 'live' },
  { key: 'enrichment',            label: 'Enrichment',            team: 'Databricks', status: 'live' },
  { key: 'silver_processing',     label: 'Silver processing',     team: 'Silver',     status: 'live' },
  { key: 'collection',            label: 'Collection',            team: 'Bronze',     status: 'live' },
  { key: 'storage',               label: 'Storage',               team: 'Cost',       status: 'live' },
  { key: 'portal',                label: 'Portal',                team: 'Portal',     status: 'live' },
  { key: 'shared_infrastructure', label: 'Shared infrastructure', team: 'Cost',       status: 'live' },
];
const COMPONENT_META = Object.fromEntries(EXPECTED_COMPONENTS.map((c) => [c.key, c]));
function componentLabel(key) { return COMPONENT_META[key]?.label || key; }

// Components that carry a per-log-type breakdown (the D37 log-type mirror). Everything
// else (storage, enrichment, portal, shared_infrastructure) has no log_type dimension and
// is shown as a single component line in the tenant drill matrix.
const LOG_DRIVEN_COMPONENTS = new Set(['ingest_compute', 'detection_streaming', 'detection_cron', 'silver_processing', 'collection']);

// For the non-log components in the tenant drill matrix, a short description of what that
// component's cost is made up of. In the matrix the component name is the Category (left
// column) and this detail is the "Line item"; falls back to the component label if unmapped.
const COMPONENT_DETAIL = {
  shared_infrastructure: 'keep-warm SQL/Log Analytics/App Insights/DataBricks Idle',
  storage: 'bronze/silver/archive/misc',
  enrichment: 'share of IP classifications',
  portal: '(Investigations)',
};

// Optional per-component caveat surfaced as a small hover info-icon next to the Category
// label in the tenant drill matrix. Kept out of the way (silent unless hovered) because it
// rarely needs conveying — see CostMatrix's `groupNote` handling.
const COMPONENT_NOTE = {
  shared_infrastructure: 'This figure comes from a not-yet-settled Azure Cost Management data point, so day-to-day fluctuations are common.',
};

// Friendly labels for the log-type (workload) dimension of the D37 log-type drill-down.
// Open-ended — the pipeline's log_type vocabulary can differ by source (see the
// Contribution Contract §Log-type drill-down caveat), so unknown values pass through raw.
const LOGTYPE_LABELS = {
  signin: 'Sign-in (Activity Feed)',
  graph_signin: 'Sign-in (Graph)',
  graph_signin_noninteractive: 'Sign-in (non-interactive)',
  exchange: 'Exchange',
  sharepoint: 'SharePoint',
  general: 'General audit',
  directory_audit: 'Directory audit',
  message_trace: 'Message trace',
  url_click: 'URL clicks',
  risk_detection: 'Risk detections',
  risky_user: 'Risky users',
  dlp: 'DLP',
  azure_activity: 'Azure activity',
  security_alert: 'Security alerts',
  security_incident: 'Security incidents',
  secure_score: 'Secure score',
  ca_policy: 'CA policies',
  device_security: 'Device security',
};
function logTypeLabel(key) { return LOGTYPE_LABELS[key] || (key || '(unknown)'); }
// $ for matrix cells — hundredths only (sub-cent values render as "$0.00"); a genuine
// zero / no-data cell renders blank so it's distinguishable from a tiny-but-nonzero cost.
function fmtUsdCell(n) {
  const v = Number(n);
  if (!v || Number.isNaN(v)) return '';
  return '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isoDate(d) { return d.toISOString().slice(0, 10); }
// "Today" anchored to America/New_York (EST/EDT), NOT UTC. The cost feed lags a day, so
// today's row is always null; we must exclude it. Anchoring on UTC gets this wrong during
// EST evening hours (after UTC has rolled to the next calendar day) — "UTC yesterday" then
// resolves to EST *today*, re-introducing the null column. en-CA formats as YYYY-MM-DD.
function estTodayIso() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
}
function rangeForPreset(days) {
  // End the window at YESTERDAY (EST), never today: today's row is null (feed lag), so
  // including it just adds an empty trailing column and dilutes the daily rate.
  const to = new Date(`${estTodayIso()}T00:00:00Z`);
  to.setUTCDate(to.getUTCDate() - 1);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { dateFrom: isoDate(from), dateTo: isoDate(to) };
}
// Continuous [from, to] day list so gaps (days with NO row) are visible, not skipped.
function enumerateDays(from, to) {
  const out = [];
  if (!from) return out;
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to || from}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return out;
  for (let d = new Date(start); d <= end && out.length < 200; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}
// Coverage state for an aggregate of rows (a day, or a grid cell).
//   priced  = all present rows priced          partial = some priced, some pending
//   pending = rows exist but all cost-TBD       none    = no rows at all
function coverageState(priced, pending) {
  if (priced > 0 && pending > 0) return 'partial';
  if (priced > 0) return 'priced';
  if (pending > 0) return 'pending';
  return 'none';
}
// Amber diagonal hatch — the trend caption's "cost-TBD" swatch chip.
const HATCH = 'repeating-linear-gradient(45deg, rgba(251,191,36,0.9) 0, rgba(251,191,36,0.9) 2px, transparent 2px, transparent 5px)';

// Full $ label; null/NaN → em dash (caller may render "cost pending" instead).
function fmtUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Compact $ for chart axes ("$1.2k").
function fmtUsdShort(n) {
  const v = Number(n || 0);
  if (v >= 1000) return '$' + (v / 1000).toFixed(1) + 'k';
  return '$' + v.toFixed(v < 10 ? 2 : 0);
}
// Number or null (blank/NaN → null so sums skip it and a chart shows a gap).
function num(n) { return (n == null || Number.isNaN(Number(n))) ? null : Number(n); }
function mdLabel(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}` : (iso || '');
}
// Saturday/Sunday? Day keys are UTC yyyy-MM-dd, so read the UTC weekday (0=Sun, 6=Sat).
function isWeekendIso(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const wd = d.getUTCDay();
  return wd === 0 || wd === 6;
}
// Subtle tint applied to weekend day columns (header/body/footer) so weekends read at a glance.
const WEEKEND_CELL = 'bg-gray-800/40';
function niceScale(max, ticks = 4) {
  if (!isFinite(max) || max <= 0) return { niceMax: 1, step: 1 };
  const rawStep = max / ticks;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  step *= mag;
  return { niceMax: Math.ceil(max / step) * step, step };
}
function windowLabel(from, to) {
  if (!from) return 'selected range';
  try {
    const d1 = new Date(`${from}T00:00:00Z`);
    const d2 = new Date(`${to || from}T00:00:00Z`);
    const days = Math.round((d2 - d1) / 86400000) + 1;
    return `last ${days} day${days === 1 ? '' : 's'}`;
  } catch { return 'selected range'; }
}
function tenantOptionLabel(t) {
  const id = t.tenantId || t.tenantID;
  return (t.shortName && t.friendlyName) ? `(${t.shortName}) ${t.friendlyName}` : (t.friendlyName || t.shortName || t.domain || id);
}

export default function TenantCostDashboard() {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const [preset, setPreset] = useState(7);
  const [mspCode, setMspCode] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [tenants, setTenants] = useState([]);
  const [showInactive, setShowInactive] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { dateFrom, dateTo } = useMemo(() => rangeForPreset(preset), [preset]);

  useEffect(() => {
    let cancelled = false;
    fetchManagedTenants()
      .then((rows) => { if (!cancelled) setTenants(Array.isArray(rows) ? rows : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadCost = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getTenantCostDaily({ dateFrom, dateTo, tenantId: tenantId || undefined });
      setData(resp);
    } catch (err) {
      setError(err.message || 'Failed to load cost data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, tenantId]);

  useEffect(() => { loadCost(); }, [loadCost]);

  const mspOptions = useMemo(() => {
    const set = new Set();
    for (const t of tenants) { const code = (t.msp || '').toUpperCase(); if (code) set.add(code); }
    return Array.from(set).sort();
  }, [tenants]);

  // Tenant status lookup (from the picker list) so the cost view can hide disabled
  // (inactive/deleted) clients by default — e.g. Medefil — and reveal them on request.
  const tenantStatus = useMemo(() => {
    const m = new Map();
    for (const t of tenants) {
      const id = String(t.tenantId || t.tenantID || '').toLowerCase();
      if (id) m.set(id, String(t.status || '').toLowerCase());
    }
    return m;
  }, [tenants]);
  const isInactiveTenant = useCallback((tid) => {
    const s = tenantStatus.get(String(tid || '').toLowerCase());
    return s === 'inactive' || s === 'deleted';
  }, [tenantStatus]);

  const tenantOptions = useMemo(() => {
    let base = !mspCode ? tenants : tenants.filter((t) => (t.msp || '').toUpperCase() === mspCode);
    if (!showInactive) base = base.filter((t) => !isInactiveTenant(t.tenantId || t.tenantID));
    return [...base].sort((a, b) =>
      tenantOptionLabel(a).localeCompare(tenantOptionLabel(b), undefined, { numeric: true, sensitivity: 'base' }));
  }, [tenants, mspCode, showInactive, isInactiveTenant]);

  // Turning the toggle off while drilled into a now-hidden tenant returns to the overview.
  const handleToggleInactive = () => {
    const next = !showInactive;
    setShowInactive(next);
    if (!next && tenantId && isInactiveTenant(tenantId)) setTenantId('');
  };

  const handleMspChange = (code) => {
    setMspCode(code);
    if (code && tenantId) {
      const t = tenants.find((x) => (x.tenantId || x.tenantID) === tenantId);
      if (!t || (t.msp || '').toUpperCase() !== code) setTenantId('');
    }
  };

  // Coarse license tier (Basic/Identity/Defender) for the drilled tenant, from the tenant
  // record (CSV) — the "easily retrievable" licensing signal shown in the profile panel.
  const selectedLicenseTier = useMemo(() => {
    if (!tenantId) return '';
    const t = tenants.find((x) => String(x.tenantId || x.tenantID).toLowerCase() === String(tenantId).toLowerCase());
    return t?.licenseTier || '';
  }, [tenants, tenantId]);

  // Friendly label for a tenant id, from the response's tenants map (falls back to the picker list).
  const tenantLabel = useCallback((tid) => {
    const meta = data?.tenants?.[String(tid || '').toLowerCase()];
    if (meta?.friendlyName) return meta.friendlyName;
    const t = tenants.find((x) => String(x.tenantId || x.tenantID).toLowerCase() === String(tid).toLowerCase());
    return t ? tenantOptionLabel(t) : tid;
  }, [data, tenants]);

  // Client-side aggregation of the flat component rows.
  const drill = !!tenantId;
  const agg = useMemo(() => {
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    const byDay = {}, byTenant = {}, byComponent = {};
    let periodTotal = 0, pendingRows = 0, pricedRows = 0;
    const tenantSet = new Set();
    // Coverage matrix: dimension (component in drill / tenant in overview) × day.
    const cellMap = {};               // `${dimKey}||${day}` -> { priced, pending, usd }
    // MSP overview filter: the API returns every authorized tenant regardless of the MSP
    // picker, so scope the rows to the selected MSP's tenants client-side (skip in drill —
    // the data is already one tenant then).
    const mspAllowed = (!drill && mspCode)
      ? new Set(tenants
          .filter((t) => (t.msp || '').toUpperCase() === mspCode)
          .map((t) => String(t.tenantId || t.tenantID).toLowerCase()))
      : null;
    const inactiveInScope = new Set();
    for (const r of rows) {
      if (mspAllowed && !mspAllowed.has(String(r.tenantId).toLowerCase())) continue;
      // Hide disabled (inactive/deleted) clients from the overview rows AND totals by default
      // (keeps the matrix reconciled: visible rows still sum to the grand total + projection).
      // The "Show inactive" toggle reveals them; the single-tenant drill is exempt (requested).
      if (!drill && isInactiveTenant(r.tenantId)) {
        inactiveInScope.add(String(r.tenantId).toLowerCase());
        if (!showInactive) continue;
      }
      const usd = (r.allocatedUsd == null) ? null : Number(r.allocatedUsd);
      if (usd == null) pendingRows++; else { periodTotal += usd; pricedRows++; }
      tenantSet.add(r.tenantId);

      if (!byDay[r.costDate]) byDay[r.costDate] = { usd: 0, priced: 0, pending: 0 };
      const dd = byDay[r.costDate];
      if (usd == null) dd.pending++; else { dd.usd += usd; dd.priced++; }

      if (!byTenant[r.tenantId]) byTenant[r.tenantId] = { tenantId: r.tenantId, usd: 0, pending: 0 };
      if (usd == null) byTenant[r.tenantId].pending++; else byTenant[r.tenantId].usd += usd;
      if (!byComponent[r.costComponent]) byComponent[r.costComponent] = { component: r.costComponent, usd: 0, pending: 0 };
      if (usd == null) byComponent[r.costComponent].pending++; else byComponent[r.costComponent].usd += usd;

      const dimKey = drill ? r.costComponent : r.tenantId;
      const ck = `${dimKey}||${r.costDate}`;
      if (!cellMap[ck]) cellMap[ck] = { priced: 0, pending: 0, usd: 0 };
      const cc = cellMap[ck];
      if (usd == null) cc.pending++; else { cc.priced++; cc.usd += usd; }
    }
    const tenantsRanked = Object.values(byTenant).sort((a, b) => b.usd - a.usd);
    const components = Object.values(byComponent).sort((a, b) => b.usd - a.usd);

    // Continuous day series for the trend + coverage columns (reveals gaps).
    const days = enumerateDays(dateFrom, dateTo);
    const series = days.map((day) => {
      const d = byDay[day];
      return {
        day,
        usd: d ? d.usd : 0,
        state: coverageState(d ? d.priced : 0, d ? d.pending : 0),
      };
    });
    const pendingDays = series.filter((s) => s.state === 'pending').length;
    const partialDays = series.filter((s) => s.state === 'partial').length;

    return {
      rowCount: pricedRows + pendingRows, periodTotal, pendingRows, pricedRows, priced: pricedRows > 0,
      tenantCount: tenantSet.size,
      dayCount: Object.keys(byDay).length, tenantsRanked,
      topComponent: components[0] ? componentLabel(components[0].component) : '—',
      days, series, pendingDays, partialDays, cellMap,
      inactiveInScope: inactiveInScope.size,
    };
  }, [data, drill, dateFrom, dateTo, mspCode, tenants, showInactive, isInactiveTenant]);

  // Overview matrix: tenant (row) × day (column), from the already-aggregated cellMap.
  // Column totals = the day's priced total across tenants (agg.series); grand = period total.
  const overviewMatrix = useMemo(() => {
    const days = agg.days;
    const rows = agg.tenantsRanked.map((t) => {
      const cells = {};
      for (const day of days) {
        const c = agg.cellMap[`${t.tenantId}||${day}`];
        if (c && c.usd) cells[day] = c.usd;
      }
      return { key: t.tenantId, label: tenantLabel(t.tenantId), cells, total: t.usd };
    });
    const colTotals = {};
    for (const s of agg.series) { if (s.usd) colTotals[s.day] = s.usd; }
    return { days, rows, colTotals, grand: agg.periodTotal };
  }, [agg, tenantLabel]);

  const available = data?.available !== false;

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <CurrencyDollarIcon className="h-7 w-7 text-emerald-500" />
          <h1 className="text-2xl font-bold text-gray-100">Cost</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Per-tenant, per-day cost-to-serve from the shared cost model. A tenant's daily cost is the sum
          of its cost components; components are onboarded over time.
        </p>
      </div>

      {/* ALPHA banner — always visible, every state. Internal estimate, NOT client charges. */}
      <div className="mb-6 rounded-lg border-2 border-amber-500/60 bg-amber-900/25 px-4 py-3 flex items-start gap-3">
        <ExclamationTriangleIcon className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-100">
          <div className="font-bold uppercase tracking-wide text-amber-300">Alpha — internal cost estimate</div>
          <p className="mt-0.5 text-amber-100/90">
            These figures are an approximate, internal <strong>cost-to-serve</strong> estimate for capacity planning.
            They are <strong>incomplete</strong> — cost components are still being onboarded, so totals understate
            actual cost — and they do <strong>NOT represent client charges, invoices, or pricing</strong>.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="card mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                preset === p.id ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-700 text-gray-400 hover:text-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          <span className="ml-2 text-xs text-gray-600">{dateFrom} → {dateTo}</span>
        </div>

        {mspOptions.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">MSP</label>
            <select value={mspCode} onChange={(e) => handleMspChange(e.target.value)} className="input-field h-9 py-0 text-sm">
              <option value="">All MSPs</option>
              {mspOptions.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Tenant</label>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="input-field h-9 py-0 text-sm min-w-[16rem]">
            <option value="">{mspCode ? `All tenants in ${mspCode}` : 'All tenants'}</option>
            {tenantOptions.map((t) => {
              const id = t.tenantId || t.tenantID;
              return <option key={id} value={id}>{tenantOptionLabel(t)}</option>;
            })}
          </select>
        </div>

        {(agg.inactiveInScope > 0 || showInactive) && (
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none" title="Disabled (inactive/deleted) clients are hidden by default">
            <input type="checkbox" checked={showInactive} onChange={handleToggleInactive} className="accent-blue-500" />
            Show inactive{agg.inactiveInScope > 0 ? ` (${agg.inactiveInScope})` : ''}
          </label>
        )}

        <RefreshButton loading={loading} onClick={loadCost} className="btn-secondary flex items-center gap-1.5 text-xs ml-auto" />
      </div>

      <RefreshingBanner show={loading && !!data} message="Refreshing cost data…" />

      {/* Global (admin-only) Azure bill coverage / reconciliation — overview only. */}
      {isGlobalAdmin && !tenantId && (
        <ReconciliationPanel dateFrom={dateFrom} dateTo={dateTo} />
      )}

      {/* Tenant size/profile — drill only. Identity data is independent of cost rows,
          so this shows on any drilled tenant even when no cost has been fed yet. */}
      {tenantId && (
        <TenantProfilePanel
          tenantId={tenantId}
          snapshotDate={dateTo}
          tenantName={tenantLabel(tenantId)}
          licenseTier={selectedLicenseTier}
        />
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={loadCost} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {loading && !data && <LoadingCard message="Loading cost data…" />}

      {data && !available && (
        <div className="card text-sm text-gray-400 flex items-center gap-2">
          <SparklesIcon className="h-4 w-4 text-gray-600" />
          The cost data table isn&apos;t available yet — the Databricks cost pipeline may not have run. Check back later.
        </div>
      )}

      {data && available && (
        <div className={dataFadeClass(loading)}>
          {agg.rowCount === 0 ? (
            <div className="card text-sm text-gray-500">No cost data for the selected range.</div>
          ) : (
            <>
              {/* Headline cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <HeadlineCard label={`Total cost — ${windowLabel(dateFrom, dateTo)}`} value={agg.priced ? fmtUsd(agg.periodTotal) : 'Pending'} accent={agg.priced ? 'text-emerald-400' : 'text-amber-300'} sub={agg.priced ? undefined : 'awaiting cost feed'} icon={CurrencyDollarIcon} />
                {tenantId ? (
                  <HeadlineCard label="Days with data" value={agg.dayCount} accent="text-gray-100" />
                ) : (
                  <HeadlineCard label="Tenants with cost" value={agg.tenantCount} accent="text-blue-400" />
                )}
                <HeadlineCard label="Top component" value={agg.topComponent} accent="text-purple-300" small />
                <HeadlineCard label="Cost-pending rows" value={agg.pendingRows} accent={agg.pendingRows > 0 ? 'text-amber-300' : 'text-gray-100'} sub={agg.pendingRows > 0 ? 'awaiting cost feed' : undefined} />
              </div>

              {/* Per-day trend — TBD days are drawn distinctly, never faked as $0. */}
              <div className="card mb-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h2 className="text-sm font-semibold text-gray-300">
                    {tenantId ? `Daily cost — ${tenantLabel(tenantId)}` : 'Total cost per day'}
                  </h2>
                  {(agg.pendingDays > 0 || agg.partialDays > 0) && (
                    <span className="text-[11px] text-amber-300 flex items-center gap-1">
                      <span className="inline-block h-2.5 w-4 rounded-sm border border-amber-500/60" style={{ backgroundImage: HATCH }} />
                      {agg.pendingDays > 0 && `${agg.pendingDays} day${agg.pendingDays === 1 ? '' : 's'} cost-TBD`}
                      {agg.pendingDays > 0 && agg.partialDays > 0 && ' · '}
                      {agg.partialDays > 0 && `${agg.partialDays} partial`}
                    </span>
                  )}
                </div>
                {agg.series.length === 0 ? (
                  <p className="text-sm text-gray-600">No cost in this window.</p>
                ) : (
                  <CostTrendChart series={agg.series} />
                )}
              </div>

              {/* Cost matrix — rows = {log type | tenant}, columns = each day in the
                  window, a Total column on the right and a Total row on the bottom. */}
              {tenantId ? (
                <TenantCostMatrix
                  tenantId={tenantId}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  tenantName={tenantLabel(tenantId)}
                  componentRows={data.rows}
                />
              ) : (
                <div className="card mb-6">
                  <h2 className="text-sm font-semibold text-gray-300 mb-1">Cost by Tenant per Day</h2>
                  <p className="text-xs text-gray-600 mb-3">
                    Each cell is one tenant&apos;s total cost (all components) on one day (today excluded — the cost feed
                    lags a day). Click a tenant to drill into its log-type breakdown. Row totals on the right, day totals
                    on the bottom, and a <span className="text-sky-300">Projected 30-Day</span> column that scales each
                    total to 30 days at the window&apos;s daily rate; blank = no cost that day.
                  </p>
                  <CostMatrix
                    days={overviewMatrix.days}
                    rows={overviewMatrix.rows}
                    colTotals={overviewMatrix.colTotals}
                    grand={overviewMatrix.grand}
                    labelHeader="Tenant"
                    onRowClick={(r) => setTenantId(r.key)}
                    empty="No cost in this window."
                    showProjection
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HeadlineCard({ label, value, sub, accent = 'text-gray-100', small = false, icon: Icon }) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 text-gray-600" />}
        <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      </div>
      <div className={`${small ? 'text-lg' : 'text-3xl'} font-bold ${accent} ${small ? 'truncate' : ''}`} title={small ? String(value) : undefined}>{value}</div>
      {sub && <div className="text-[10px] text-gray-600 mt-1">{sub}</div>}
    </div>
  );
}

// Coarse license-tier badge tone (Basic/Identity/Defender). Unknown/empty → neutral.
function licenseTierTone(tier) {
  switch (String(tier || '').toLowerCase()) {
    case 'defender': return 'bg-purple-900/40 text-purple-200 border border-purple-700/50';
    case 'identity': return 'bg-blue-900/40 text-blue-200 border border-blue-700/50';
    case 'basic':    return 'bg-gray-700/60 text-gray-200 border border-gray-600/50';
    default:         return 'bg-gray-800 text-gray-400 border border-gray-700';
  }
}

// Tenant size/profile panel — drill-only. Gives cost figures context by showing how big
// the tenant is: active AAD users, group count, licensing (coarse tier now; per-SKU once
// the pipeline feeds assigned_licenses_json), and the Global Administrator roster. Data is
// aggregated server-side (GetTenantProfile) over the daily Entra identity snapshot; lazy
// fetch on first expand so the extra call only happens when an analyst wants it.
function TenantProfilePanel({ tenantId, snapshotDate, tenantName, licenseTier }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [loaded, setLoaded]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await getTenantProfile({ tenantId, snapshotDate }));
      setLoaded(true);
    } catch (e) {
      setError(e.message || 'Failed to load tenant profile'); setData(null);
    } finally { setLoading(false); }
  }, [tenantId, snapshotDate]);

  // Reset when the drilled tenant changes so a stale profile isn't shown under a new tenant.
  useEffect(() => { setExpanded(false); setData(null); setLoaded(false); setError(null); }, [tenantId]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loaded && !loading) load();
  };

  const available = data?.available !== false;
  // Single-element arrays are unwrapped by PowerShell ConvertTo-Json — coerce defensively.
  const globalAdmins = Array.isArray(data?.globalAdmins)
    ? data.globalAdmins
    : (data?.globalAdmins ? [data.globalAdmins] : []);
  const users = data?.users || {};
  const licensing = data?.licensing || {};
  const licenseAvailable = licensing.available === true;
  const bySku = Array.isArray(licensing.bySku)
    ? licensing.bySku
    : (licensing.bySku ? [licensing.bySku] : []);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between gap-2">
        <button onClick={handleToggle} className="flex items-center gap-2 text-left group" title={expanded ? 'Collapse' : 'Show active users, groups, and Global Admins'}>
          {expanded ? <ChevronDownIcon className="h-4 w-4 text-gray-500" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500" />}
          <UsersIcon className="h-5 w-5 text-sky-400" />
          <h2 className="text-sm font-semibold text-gray-200 group-hover:text-gray-100">
            Tenant profile{tenantName ? ` — ${tenantName}` : ''}
          </h2>
          {!expanded && <span className="text-xs text-gray-600">active users · groups · Global Admins</span>}
        </button>
        {expanded && loaded && (
          <RefreshButton loading={loading} onClick={load} className="btn-secondary flex items-center gap-1.5 text-xs" />
        )}
      </div>

      {expanded && (
        <div className="mt-4">
          {error && (
            <div className="mb-3 p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
              <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
            </div>
          )}
          {loading && !data && <div className="text-sm text-gray-500 py-4">Loading tenant profile…</div>}

          {data && !available && (
            <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
              <SparklesIcon className="h-4 w-4 text-gray-600" />
              Identity data isn&apos;t available yet — the Databricks Entra snapshot pipeline may not have run.
            </div>
          )}
          {data && available && !data.snapshotDate && (
            <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
              <SparklesIcon className="h-4 w-4 text-gray-600" />
              No recent identity snapshot for this tenant.
            </div>
          )}

          {data && available && data.snapshotDate && (
            <div className={dataFadeClass(loading)}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <HeadlineCard
                  label="Active users"
                  value={Number(users.active || 0).toLocaleString()}
                  accent="text-sky-300"
                  sub={`of ${Number(users.total || 0).toLocaleString()} total${users.guests ? ` · ${Number(users.guests).toLocaleString()} guests` : ''}`}
                  icon={UsersIcon}
                />
                <HeadlineCard label="Groups" value={Number(data.groupCount || 0).toLocaleString()} accent="text-gray-100" />
                {/* Licensing — coarse tier badge + assigned-license counts (per-SKU list below). */}
                <div className="card">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs uppercase tracking-wide text-gray-500">Licensing</span>
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded text-sm font-semibold ${licenseTierTone(licenseTier)}`}>
                    {licenseTier || 'Not set'}
                  </span>
                  {licenseAvailable ? (
                    <div className="text-[10px] text-gray-500 mt-1.5">
                      <span className="text-gray-300 font-semibold">{Number(licensing.licensed || 0).toLocaleString()}</span> licensed
                      {Number(licensing.unlicensed || 0) > 0 && <> · {Number(licensing.unlicensed).toLocaleString()} unlicensed</>}
                    </div>
                  ) : (
                    <div className="text-[10px] text-gray-600 mt-1.5">Per-user license breakdown pending pipeline data</div>
                  )}
                </div>
                <HeadlineCard label="Global Admins" value={Number(data.globalAdminCount || 0).toLocaleString()} accent={Number(data.globalAdminCount || 0) > 0 ? 'text-amber-300' : 'text-gray-100'} icon={ShieldCheckIcon} />
              </div>

              {globalAdmins.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
                    <ShieldCheckIcon className="h-4 w-4 text-amber-400" /> Global Administrators
                  </div>
                  <ul className="text-xs text-gray-300 space-y-0.5">
                    {globalAdmins.map((g, i) => (
                      <li key={`${g.upn || g.displayName || i}`} className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-mono text-gray-200">{g.upn || '(no UPN)'}</span>
                        {g.displayName && g.displayName !== g.upn && <span className="text-gray-500">— {g.displayName}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {licenseAvailable && bySku.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
                    <KeyIcon className="h-4 w-4 text-sky-400" /> License assignments
                  </div>
                  <ul className="text-xs text-gray-300 space-y-0.5 max-w-lg">
                    {bySku.map((s, i) => {
                      const name = s.name;  // resolved server-side from the shared licensing CSV
                      return (
                        <li key={`${s.skuId || i}`} className="flex items-baseline justify-between gap-x-3">
                          {name ? (
                            <span className="text-gray-200">{name}</span>
                          ) : (
                            <span className="font-mono text-gray-400 break-all" title="SKU not in the shared licensing CSV yet">
                              {s.skuId} <span className="text-gray-600">(unknown SKU)</span>
                            </span>
                          )}
                          <span className="text-gray-400 tabular-nums flex-shrink-0">{Number(s.count || 0).toLocaleString()}</span>
                        </li>
                      );
                    })}
                    {Number(licensing.unlicensed || 0) > 0 && (
                      <li className="flex items-baseline justify-between gap-x-3 text-gray-500 border-t border-gray-800 mt-1 pt-1">
                        <span>Unlicensed</span>
                        <span className="tabular-nums flex-shrink-0">{Number(licensing.unlicensed).toLocaleString()}</span>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              <p className="text-[11px] text-gray-600 mt-3">
                As of the {data.snapshotDate} identity snapshot. Active = enabled AAD accounts.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Line chart of daily total $. TBD days are NOT faked as $0 — the line breaks across
// them and a hatched band + hollow marker sits at baseline so missing cost is obvious.
// `series`: [{ day, usd, state: 'priced'|'partial'|'pending'|'none' }].
function CostTrendChart({ series }) {
  const pts = Array.isArray(series) ? series : [];
  const n = pts.length;
  const W = 760, H = 220, padL = 56, padR = 16, padT = 12, padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const baseY = H - padB;

  const plottable = (s) => s.state === 'priced' || s.state === 'partial';
  const maxVal = Math.max(0.01, ...pts.filter(plottable).map((d) => Number(d.usd) || 0));
  const { niceMax, step } = niceScale(maxVal, 4);
  const yTicks = [];
  for (let v = 0; v <= niceMax + 1e-9; v += step) yTicks.push(v);

  const xAt = (i) => (n <= 1 ? padL + plotW / 2 : padL + (i * plotW) / (n - 1));
  const yAt = (v) => padT + plotH - ((Number(v) || 0) / niceMax) * plotH;
  const colW = n <= 1 ? 40 : plotW / (n - 1);
  const bandW = Math.min(colW * 0.7, 18);

  // Connected runs of consecutive plottable points (break the line at TBD/no-data days).
  const runs = [];
  let cur = [];
  pts.forEach((s, i) => {
    if (plottable(s)) cur.push(i);
    else { if (cur.length) runs.push(cur); cur = []; }
  });
  if (cur.length) runs.push(cur);

  const maxLabels = 8;
  const everyX = Math.max(1, Math.ceil(n / maxLabels));
  const showX = (i) => i === n - 1 || i % everyX === 0;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="select-none" style={{ maxHeight: '240px' }}>
      <defs>
        <pattern id="tbdHatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill="rgba(251,191,36,0.05)" />
          <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(251,191,36,0.45)" strokeWidth="1.5" />
        </pattern>
      </defs>

      {/* TBD bands behind the grid */}
      {pts.map((d, i) => (d.state === 'pending' ? (
        <rect key={`b-${d.day}`} x={xAt(i) - bandW / 2} y={padT} width={bandW} height={plotH} fill="url(#tbdHatch)">
          <title>{`${d.day}: cost TBD (usage recorded, dollars not yet fed)`}</title>
        </rect>
      ) : null))}

      {yTicks.map((v) => {
        const y = yAt(v);
        return (
          <g key={v}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#1f2937" strokeWidth="1" />
            <text x={padL - 8} y={y + 3} fontSize="11" fill="#9ca3af" textAnchor="end">{fmtUsdShort(v)}</text>
          </g>
        );
      })}
      <line x1={padL} y1={padT} x2={padL} y2={baseY} stroke="#4b5563" strokeWidth="1.25" />
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#4b5563" strokeWidth="1.25" />
      {pts.map((d, i) => (showX(i) ? (
        <g key={`x-${d.day}`}>
          <line x1={xAt(i)} y1={baseY} x2={xAt(i)} y2={baseY + 4} stroke="#4b5563" strokeWidth="1" />
          <text x={xAt(i)} y={baseY + 18} fontSize="11" fill="#9ca3af" textAnchor="middle">{mdLabel(d.day)}</text>
        </g>
      ) : null))}

      {/* Priced/partial line segments (broken across TBD gaps) */}
      {runs.map((run, ri) => (run.length >= 2 ? (
        <polyline key={`r-${ri}`} points={run.map((i) => `${xAt(i)},${yAt(pts[i].usd)}`).join(' ')} fill="none" stroke="#34d399" strokeWidth="2.25" />
      ) : null))}

      {/* Markers */}
      {pts.map((d, i) => {
        if (d.state === 'pending') {
          return (
            <circle key={`m-${d.day}`} cx={xAt(i)} cy={baseY} r="3.5" fill="#0b1220" stroke="#fbbf24" strokeWidth="1.5">
              <title>{`${d.day}: cost TBD`}</title>
            </circle>
          );
        }
        if (!plottable(d)) return null; // 'none' — no data collected, draw nothing
        const isPartial = d.state === 'partial';
        return (
          <circle key={`m-${d.day}`} cx={xAt(i)} cy={yAt(d.usd)} r="3.5"
            fill={isPartial ? '#fbbf24' : '#34d399'} stroke="#0b1220" strokeWidth="1.25">
            <title>{`${d.day}: ${fmtUsd(d.usd)}${isPartial ? ' (partial — some components still TBD)' : ''}`}</title>
          </circle>
        );
      })}
    </svg>
  );
}

// Generic cost matrix — rows = a dimension (tenant / log type), columns = each day in the
// window, with a Total column on the right and a Total row on the bottom (grand total in
// the corner). Horizontally scrollable; the label column and the Total column stick to the
// left/right edges so both anchors stay visible across a wide (up to 92-day) grid.
//   days     : ordered [yyyy-MM-dd] (continuous — a day with no cost renders a blank column)
//   rows     : [{ key, label, cells: {day: usd}, total }] (already sorted by the caller)
//   colTotals: {day: usd}    grand: number
//   groupHeader: optional — when set, a far-left column shows each row's `group` (printed
//                once per contiguous group; rows must be pre-sorted by group).
//   onRowClick(row): optional — makes the label clickable (overview → tenant drill).
function CostMatrix({ days, rows, colTotals, grand, labelHeader, groupHeader, onRowClick, empty, showProjection }) {
  const cols = Array.isArray(days) ? days : [];
  const dimRows = Array.isArray(rows) ? rows : [];
  if (dimRows.length === 0 || cols.length === 0) {
    return <p className="text-sm text-gray-600">{empty || 'No data for the selected range.'}</p>;
  }
  const clickable = typeof onRowClick === 'function';
  const grouped = !!groupHeader;
  const GROUP_W = '6.5rem';
  const labelLeft = grouped ? GROUP_W : 0;                 // label column sits right of the group column
  const sticky = 'sticky z-10 bg-gray-900';
  const stickyR = 'sticky right-0 z-10 bg-gray-900 border-l border-gray-700';
  const cell = 'px-2 py-1 text-right tabular-nums whitespace-nowrap';
  // Projected 30-day cost = daily rate over the window × 30. The rate uses only PRICED days
  // (a day with cost data), so a still-lagging trailing day doesn't understate it, and every
  // row shares the same factor so the column reconciles row totals → grand total.
  const PROJECT_DAYS = 30;
  const pricedDayCount = showProjection ? cols.filter((d) => Number(colTotals?.[d]) > 0).length : 0;
  const projFactor = pricedDayCount > 0 ? PROJECT_DAYS / pricedDayCount : 0;
  const projOf = (v) => (projFactor && Number(v) ? Number(v) * projFactor : null);
  // With the projection column present it becomes the sticky-right anchor and Total scrolls
  // just left of it; without it Total stays pinned as before.
  const totalHeadCls = showProjection ? 'text-right font-semibold px-2 py-2 border-l border-gray-700' : `${stickyR} text-right font-semibold px-2 py-2`;
  const totalCellCls = showProjection ? `${cell} border-l border-gray-700` : `${stickyR} ${cell}`;
  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-800">
            {grouped && <th className={`${sticky} text-left font-semibold px-2 py-2`} style={{ left: 0, minWidth: GROUP_W }}>{groupHeader}</th>}
            <th className={`${sticky} text-left font-semibold px-2 py-2`} style={{ left: labelLeft }}>{labelHeader}</th>
            {cols.map((day) => (
              <th key={day} className={`px-2 py-2 text-right font-semibold whitespace-nowrap ${isWeekendIso(day) ? WEEKEND_CELL : ''}`}>{mdLabel(day)}</th>
            ))}
            <th className={totalHeadCls}>Total</th>
            {showProjection && (
              <th
                className={`${stickyR} text-right font-semibold px-2 py-2 text-sky-300`}
                title={`30-day projection at the window's daily rate (total ÷ ${pricedDayCount || 0} priced day${pricedDayCount === 1 ? '' : 's'} × 30)`}
              >
                Projected 30-Day
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800/50">
          {dimRows.map((r, i) => {
            const newGroup = grouped && (i === 0 || dimRows[i - 1].group !== r.group);
            return (
              <tr key={r.key} className={`hover:bg-gray-800/30 ${newGroup ? 'border-t border-gray-700/70' : ''}`}>
                {grouped && (
                  <td className={`${sticky} text-left px-2 py-1 align-top text-[11px] text-gray-400 font-medium`} style={{ left: 0, minWidth: GROUP_W }}>
                    {newGroup && (
                      <span className="inline-flex items-start gap-1">
                        {r.group}
                        {r.groupNote && (
                          <span className="cursor-help text-gray-500 hover:text-gray-300" title={r.groupNote}>
                            <InformationCircleIcon className="inline h-3.5 w-3.5 align-text-top" aria-label="More info" />
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                )}
                <td
                  className={`${sticky} text-left px-2 py-1 max-w-[16rem] truncate ${clickable ? 'cursor-pointer text-blue-300 hover:text-blue-200' : 'text-gray-200'}`}
                  style={{ left: labelLeft }}
                  title={r.label}
                  onClick={clickable ? () => onRowClick(r) : undefined}
                >
                  {r.label}
                </td>
                {cols.map((day) => {
                  const v = r.cells[day];
                  return <td key={day} className={`${cell} ${v ? 'text-gray-300' : 'text-gray-700'} ${isWeekendIso(day) ? WEEKEND_CELL : ''}`}>{v ? fmtUsdCell(v) : ''}</td>;
                })}
                <td className={`${totalCellCls} font-semibold text-gray-100`}>{fmtUsdCell(r.total)}</td>
                {showProjection && (
                  <td className={`${stickyR} ${cell} font-semibold text-sky-300`}>{fmtUsdCell(projOf(r.total))}</td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-700 font-semibold">
            {grouped && <td className={sticky} style={{ left: 0, minWidth: GROUP_W }} />}
            <td className={`${sticky} text-left px-2 py-2 text-gray-300`} style={{ left: labelLeft }}>Total</td>
            {cols.map((day) => {
              const v = colTotals?.[day];
              return <td key={day} className={`${cell} ${v ? 'text-gray-200' : 'text-gray-700'} ${isWeekendIso(day) ? WEEKEND_CELL : ''}`}>{v ? fmtUsdCell(v) : ''}</td>;
            })}
            <td className={`${totalCellCls} text-emerald-400`}>{fmtUsdCell(grand)}</td>
            {showProjection && (
              <td className={`${stickyR} ${cell} text-sky-300`}>{fmtUsdCell(projOf(grand))}</td>
            )}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Single-tenant drill matrix. Two row groups reconcile to the tenant's FULL cost:
//   "Log data"  — the event/log-driven components broken out by log feed, from the D37 ATS
//                 mirror SilverM365TenantLogTypeCostDaily (getTenantLogTypeCostDaily). Those
//                 rows sum exactly to the tenant's per-component rows for those components.
//   "Other"     — the remaining components (storage, enrichment, portal, shared
//                 infrastructure), one row each, from the tenant's main cost rows
//                 (`componentRows`, from GetTenantCostDaily) — they carry no log_type.
// Grand total = log-driven (by log type) + non-log components = the tenant's total cost,
// so this matrix reconciles to the "Total cost" headline.
function TenantCostMatrix({ tenantId, dateFrom, dateTo, tenantName, componentRows }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getTenantLogTypeCostDaily({ dateFrom, dateTo, tenantId })); }
    catch (e) { setError(e.message || 'Failed to load log-type cost'); setData(null); }
    finally { setLoading(false); }
  }, [tenantId, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const matrix = useMemo(() => {
    const ltRows = Array.isArray(data?.rows) ? data.rows : [];
    const compRows = Array.isArray(componentRows) ? componentRows : [];
    const days = enumerateDays(dateFrom, dateTo);
    const dayKnown = new Set(days);
    const colTotals = {};
    let grand = 0;
    const add = (day, usd) => { colTotals[day] = (colTotals[day] || 0) + usd; grand += usd; };

    // "Log data" — log-driven components broken out by log feed.
    const byLt = {};
    for (const r of ltRows) {
      const usd = num(r.allocatedUsd);
      if (usd == null || !dayKnown.has(r.costDate)) continue; // matrix shows priced $
      const key = r.logType || '(unknown)';
      if (!byLt[key]) byLt[key] = { key: `lt:${key}`, group: 'M365 Log data', label: logTypeLabel(key), cells: {}, total: 0 };
      byLt[key].cells[r.costDate] = (byLt[key].cells[r.costDate] || 0) + usd;
      byLt[key].total += usd;
      add(r.costDate, usd);
    }

    // "Other" — the non-log components, one row each (skip log-driven ones — already
    // represented by their log-feed rows above, so no double counting).
    const byComp = {};
    for (const r of compRows) {
      if (LOG_DRIVEN_COMPONENTS.has(r.costComponent)) continue;
      const usd = num(r.allocatedUsd);
      if (usd == null || !dayKnown.has(r.costDate)) continue;
      const key = r.costComponent || '(unknown)';
      // Category = the component name; Line item = its makeup detail (or the label if unmapped).
      if (!byComp[key]) byComp[key] = { key: `c:${key}`, group: componentLabel(key), groupNote: COMPONENT_NOTE[key], label: COMPONENT_DETAIL[key] || componentLabel(key), cells: {}, total: 0 };
      byComp[key].cells[r.costDate] = (byComp[key].cells[r.costDate] || 0) + usd;
      byComp[key].total += usd;
      add(r.costDate, usd);
    }

    const logRows = Object.values(byLt).sort((a, b) => b.total - a.total);
    const otherRows = Object.values(byComp).sort((a, b) => b.total - a.total);
    const rows = [...logRows, ...otherRows];
    return { days, rows, colTotals, grand, hasRows: rows.length > 0 };
  }, [data, componentRows, dateFrom, dateTo]);

  const available = data?.available !== false;

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-gray-300">Cost by Line Item per Day{tenantName ? ` — ${tenantName}` : ''}</h2>
        <RefreshButton loading={loading} onClick={load} className="btn-secondary flex items-center gap-1.5 text-xs" />
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Each cell is one cost line item on one day. <span className="text-gray-400">M365 Log data</span> = the event/log-driven
        components (ingest, detection, silver processing, collection) broken out by log feed; the remaining categories are the
        other cost components (storage, enrichment, portal, shared infrastructure), with their makeup as the line item.
        Row totals on the right, day totals on the bottom, and a <span className="text-sky-300">Projected 30-Day</span> column
        (each line item scaled to 30 days at the window&apos;s daily rate); the grand total reconciles to this tenant&apos;s total cost.
      </p>

      {error && (
        <div className="mb-3 p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}
      {loading && !data && <div className="text-sm text-gray-500 py-4">Loading cost…</div>}
      {!loading && !matrix.hasRows && (
        <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
          <SparklesIcon className="h-4 w-4 text-gray-600" />
          {data && !available
            ? 'Cost data isn’t available yet — the Databricks cost pipeline may not have run.'
            : 'No cost data for the selected range.'}
        </div>
      )}
      {matrix.hasRows && (
        <div className={dataFadeClass(loading)}>
          <CostMatrix
            days={matrix.days}
            rows={matrix.rows}
            colTotals={matrix.colTotals}
            grand={matrix.grand}
            groupHeader="Category"
            labelHeader="Line item"
            empty="No cost."
            showProjection
          />
        </div>
      )}
    </div>
  );
}

// D36 GLOBAL reconciliation panel (admin-only, overview): compares the authoritative
// Azure spend tagged project=M365Signal (top-down) to what the model captures and splits
// per tenant (bottom-up). Reads SilverM365CostReconciliationDaily via getCostReconciliation.
// A coverage aid, NOT a client invoice. Self-contained fetch keyed to the page's date range.
function ReconciliationPanel({ dateFrom, dateTo }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setRec(await getCostReconciliation({ dateFrom, dateTo })); }
    catch (e) { setError(e.message || 'Failed to load reconciliation'); setRec(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const agg = useMemo(() => {
    const rows = Array.isArray(rec?.rows) ? rec.rows : [];
    let tag = 0, scope = 0, alloc = 0, outScopeTotal = 0, gapDays = 0;
    const series = [];
    for (const r of rows) {
      const t = num(r.azureTagTotalUsd), s = num(r.modeledScopeTotalUsd), a = num(r.allocatedTotalUsd);
      if (t != null) tag += t;
      if (s != null) scope += s;
      if (a != null) alloc += a;
      const v = num(r.varianceTagVsScopeUsd);          // tag − scope; > 0 = tagged cost outside the model
      if (v != null && v > 0) { outScopeTotal += v; if (v >= 0.5) gapDays++; }
      series.push({ day: r.costDate, tag: t, scope: s, alloc: a });
    }
    const coverage = tag > 0 ? (alloc / tag) * 100 : null;
    const avgGapPerDay = rows.length > 0 ? outScopeTotal / rows.length : 0;
    return { rowCount: rows.length, tag, scope, alloc, coverage, outScopeTotal, gapDays, avgGapPerDay, series };
  }, [rec]);

  const available = rec?.available !== false;
  const covAccent = agg.coverage == null ? 'text-gray-100'
    : agg.coverage >= 90 ? 'text-emerald-400'
    : agg.coverage >= 60 ? 'text-amber-300' : 'text-red-400';

  return (
    <div className="card mb-6 border-gray-700/60">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ScaleIcon className="h-5 w-5 text-emerald-400" />
          <h2 className="text-sm font-semibold text-gray-200">Azure bill coverage — reconciliation</h2>
          <span className="text-[10px] uppercase tracking-wide text-gray-600 border border-gray-700 rounded px-1.5 py-0.5">global</span>
        </div>
        <RefreshButton loading={loading} onClick={load} className="btn-secondary flex items-center gap-1.5 text-xs" />
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Top-down check: authoritative Azure spend tagged <code className="text-gray-400">project=M365Signal</code> vs. what the
        model captures and splits per tenant, over {windowLabel(dateFrom, dateTo)}. A coverage aid — not a client invoice.
      </p>

      {error && (
        <div className="mb-3 p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}
      {loading && !rec && <div className="text-sm text-gray-500 py-4">Loading reconciliation…</div>}
      {rec && !available && (
        <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
          <SparklesIcon className="h-4 w-4 text-gray-600" />
          Reconciliation data isn&apos;t available yet — the Databricks cost pipeline may not have run.
        </div>
      )}
      {rec && available && agg.rowCount === 0 && (
        <div className="text-sm text-gray-500 py-2">No reconciliation data for the selected range.</div>
      )}

      {rec && available && agg.rowCount > 0 && (
        <div className={dataFadeClass(loading)}>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <HeadlineCard label="Azure (tagged)" value={fmtUsd(agg.tag)} accent="text-gray-100" icon={CurrencyDollarIcon} />
            <HeadlineCard label="Modeled (in scope)" value={fmtUsd(agg.scope)} accent="text-blue-300" />
            <HeadlineCard label="Allocated (per tenant)" value={fmtUsd(agg.alloc)} accent="text-emerald-400" />
            <HeadlineCard label="Coverage (allocated ÷ tagged)" value={agg.coverage == null ? '—' : `${agg.coverage.toFixed(0)}%`} accent={covAccent} />
          </div>

          {/* Coverage gap: tagged spend outside the model (positive tag − scope variance). */}
          {agg.outScopeTotal >= 0.5 && (
            <div className="mb-4 p-3 rounded border border-amber-600/50 bg-amber-900/20 text-amber-100 text-sm flex items-start gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">{fmtUsd(agg.outScopeTotal)}</span> of tagged spend outside the model over {windowLabel(dateFrom, dateTo)}
                {' '}(~{fmtUsd(agg.avgGapPerDay)}/day). This is cost tagged <code className="text-amber-200/90">project=M365Signal</code> but outside the
                model&apos;s resource-group scope — typically the portal&apos;s managed resource groups. Review if it grows unexpectedly.
              </div>
            </div>
          )}

          {/* Daily stacked bar: allocated + in-scope-unallocated + out-of-scope = tagged */}
          <ReconStackedDailyChart series={agg.series} />

          <p className="text-[11px] text-gray-600 mt-3">
            Each bar totals the tagged Azure spend that day; the amber slice is in-scope cost not yet split per tenant, the red
            slice is tagged cost outside the model (a coverage gap). A <em>negative</em> tag − scope on a day usually means tagging
            lagged that day, not that we over-scoped.
          </p>
        </div>
      )}
    </div>
  );
}

// Per-day stacked bars: allocated (emerald) + in-scope-unallocated (amber) + out-of-scope
// (red) = the day's tagged Azure spend. The colored stack above the emerald base = the gap.
// `series`: [{ day, tag, scope, alloc }] (any value may be null on a lagged day).
function ReconStackedDailyChart({ series }) {
  const pts = (Array.isArray(series) ? series : []).map((s) => {
    const a = Math.max(0, num(s.alloc) ?? 0);
    const sc = num(s.scope);
    const t = num(s.tag);
    // Split allocated at the modeled (scope) line so an inversion (allocated > modeled)
    // renders as a distinct hatched "over-allocated" slice instead of a silent overshoot.
    const allocInScope = sc != null ? Math.min(a, sc) : a;          // emerald — allocated within model scope
    const overAlloc    = sc != null ? Math.max(0, a - sc) : 0;      // hatched — allocated beyond modeled (anomaly)
    const unalloc      = sc != null ? Math.max(0, sc - a) : 0;      // amber — in-scope, not yet split
    const outscope     = (t != null && sc != null) ? Math.max(0, t - sc) : 0; // red — tagged, outside model
    const hasData = (num(s.alloc) != null) || (sc != null) || (t != null);
    return { day: s.day, allocInScope, overAlloc, unalloc, outscope,
             total: allocInScope + overAlloc + unalloc + outscope, hasData };
  });
  const hasOverAlloc = pts.some((d) => d.overAlloc > 0);
  const n = pts.length;
  const W = 760, H = 220, padL = 56, padR = 16, padT = 12, padB = 36;
  const plotW = W - padL - padR, plotH = H - padT - padB, baseY = H - padB;
  const maxVal = Math.max(0.01, ...pts.map((d) => d.total));
  const { niceMax, step } = niceScale(maxVal, 4);
  const yTicks = [];
  for (let v = 0; v <= niceMax + 1e-9; v += step) yTicks.push(v);

  const colW = n <= 1 ? 40 : plotW / n;
  const barW = Math.min(colW * 0.7, 22);
  const xCenter = (i) => padL + colW * (i + 0.5);
  const hAt = (v) => ((Number(v) || 0) / niceMax) * plotH;

  const maxLabels = 8;
  const everyX = Math.max(1, Math.ceil(n / maxLabels));
  const showX = (i) => i === n - 1 || i % everyX === 0;

  // Bottom→top stack. `fill` is an SVG paint (solid color or the over-alloc pattern);
  // `swatch` is the matching CSS for the legend chip.
  const OVERALLOC_SWATCH = { backgroundColor: '#34d399', backgroundImage: 'repeating-linear-gradient(45deg, rgba(185,28,28,0.95) 0, rgba(185,28,28,0.95) 2px, transparent 2px, transparent 5px)' };
  const SEG = [
    { key: 'allocInScope', label: 'Allocated',                   fill: '#34d399',              swatch: { backgroundColor: '#34d399' } },
    { key: 'overAlloc',    label: 'Over-allocated (> modeled)',  fill: 'url(#overAllocHatch)', swatch: OVERALLOC_SWATCH },
    { key: 'unalloc',      label: 'In-scope, unallocated',       fill: '#fbbf24',              swatch: { backgroundColor: '#fbbf24' } },
    { key: 'outscope',     label: 'Out of scope (coverage gap)', fill: '#f87171',              swatch: { backgroundColor: '#f87171' } },
  ];

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="select-none" style={{ maxHeight: '240px' }}>
        <defs>
          {/* Emerald base + red diagonal stripes — "allocated, but beyond what we modeled". */}
          <pattern id="overAllocHatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill="#34d399" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#b91c1c" strokeWidth="2.5" />
          </pattern>
        </defs>
        {yTicks.map((v) => {
          const y = baseY - hAt(v);
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#1f2937" strokeWidth="1" />
              <text x={padL - 8} y={y + 3} fontSize="11" fill="#9ca3af" textAnchor="end">{fmtUsdShort(v)}</text>
            </g>
          );
        })}
        <line x1={padL} y1={padT} x2={padL} y2={baseY} stroke="#4b5563" strokeWidth="1.25" />
        <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#4b5563" strokeWidth="1.25" />

        {pts.map((d, i) => {
          if (!d.hasData || d.total <= 0) return null;
          const x = xCenter(i) - barW / 2;
          let yTop = baseY;
          const rects = [];
          for (const seg of SEG) {
            const val = d[seg.key];
            if (val <= 0) continue;
            const h = hAt(val);
            yTop -= h;
            rects.push(
              <rect key={seg.key} x={x} y={yTop} width={barW} height={h} fill={seg.fill} rx="1">
                <title>{`${d.day} · ${seg.label}: ${fmtUsd(val)}`}</title>
              </rect>
            );
          }
          return <g key={d.day}>{rects}</g>;
        })}

        {pts.map((d, i) => (showX(i) ? (
          <g key={`x-${d.day}`}>
            <line x1={xCenter(i)} y1={baseY} x2={xCenter(i)} y2={baseY + 4} stroke="#4b5563" strokeWidth="1" />
            <text x={xCenter(i)} y={baseY + 18} fontSize="11" fill="#9ca3af" textAnchor="middle">{mdLabel(d.day)}</text>
          </g>
        ) : null))}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-gray-400">
        {SEG.filter((s) => s.key !== 'overAlloc' || hasOverAlloc).map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={s.swatch} />
            {s.label}
          </span>
        ))}
      </div>
      {hasOverAlloc && (
        <p className="text-[11px] text-red-300/90 mt-1">
          Striped green = allocated cost exceeding the modeled in-scope total on one or more days — a pricing-source
          mismatch worth checking (allocated should never exceed what the model captures).
        </p>
      )}
    </div>
  );
}
